import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { LoginService } from './login.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})

export class LoginComponent implements OnInit, OnDestroy {
  usermail: string = '';
  password: string = '';
  errorMessage: string = '';
  notRobot: boolean = false;
  showPassword: boolean = false;
  isLoading: boolean = false;
  captchaResolved: boolean = false;
  private destroy$ = new Subject<void>();
  isMobile: boolean = false;
  isMobileMenuOpen: boolean = false;
  hcaptchaSiteKey: string = '08da61cc-ce48-4bd2-b5de-9b30eee63afe'; 
  captchaToken: string = ''; 

  constructor(
    private router: Router, 
    private loginService: LoginService, 
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.manageBodyAndNavbarClasses(true);
    this.checkViewport();
    this.setupClickOutsideListener();
  }

  ngOnDestroy() {
    this.manageBodyAndNavbarClasses(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private manageBodyAndNavbarClasses(add: boolean) {
    const body = document.getElementsByTagName('body')[0];
    const navbar = document.getElementsByTagName('nav')[0];
    if (body) {
      if (add) body.classList.add('full-screen', 'register');
      else body.classList.remove('full-screen', 'register');
    }
    if (navbar) {
      if (add) navbar.classList.add('navbar-transparent');
      else navbar.classList.remove('navbar-transparent');
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkViewport();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const mobileMenu = document.querySelector('.nav-links');
    const toggleButton = document.querySelector('.mobile-menu-toggle');
    
    // Close menu if clicking outside
    if (this.isMobileMenuOpen && 
        mobileMenu && 
        !mobileMenu.contains(target) && 
        toggleButton && 
        !toggleButton.contains(target)) {
      this.closeMobileMenu();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    // Close mobile menu on scroll
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  private checkViewport() {
    const newIsMobile = window.innerWidth <= 768;
    
    // If switching from mobile to desktop, close menu
    if (this.isMobile && !newIsMobile && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
    
    this.isMobile = newIsMobile;
  }

  private setupClickOutsideListener() {
    // Additional setup for click outside handling if needed
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  // Mobile menu methods
  toggleMobileMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    
    // Toggle active class on nav-links
    const navLinks = document.querySelector('.nav-links');
    if (navLinks) {
      if (this.isMobileMenuOpen) {
        navLinks.classList.add('active');
      } else {
        navLinks.classList.remove('active');
      }
    }
    
    // Prevent body scroll when menu is open on mobile
    if (this.isMobile) {
      document.body.style.overflow = this.isMobileMenuOpen ? 'hidden' : '';
    }
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    const navLinks = document.querySelector('.nav-links');
    if (navLinks) {
      navLinks.classList.remove('active');
    }
    // Restore body scroll
    document.body.style.overflow = '';
  }

  // hCaptcha callback methods
  onCaptchaVerify(token: string) {
    this.errorMessage = '';
    this.captchaToken = token;
    this.captchaResolved = true;
    this.notRobot = true;
    this.toastr.success('Captcha verified successfully.', 'Verification');
    console.log('hCaptcha verified:', token);
  }

  onCaptchaExpired() {
    this.captchaToken = '';
    this.captchaResolved = false;
    this.notRobot = false;
    this.errorMessage = 'Captcha expired. Please verify again.';
    this.toastr.warning(this.errorMessage, 'Expired');
  }

  onCaptchaError(error: any) {
    this.captchaToken = '';
    this.captchaResolved = false;
    this.notRobot = false;
    this.errorMessage = 'Captcha error. Please try again.';
    this.toastr.error(this.errorMessage, 'Error');
    console.error('hCaptcha error:', error);
  }

  clearErrorMessage() {
    this.errorMessage = '';
  }

  startNewApplication() {
    this.closeMobileMenu();
    this.router.navigate(['signup']);
  }

  sendPassword(formData: any) {
    this.usermail = formData.usermail;
    if (!this.usermail?.trim()) {
      this.errorMessage = 'Please enter your E-mail address.';
      this.toastr.warning(this.errorMessage, 'Warning');
      return;
    }
    this.loginService
      .getPassword(this.usermail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (password) => {
          const response = password;
          if (response?.response) {
            const message = this.buildPasswordResetMessage(response.response);
            const mailingData = this.buildMailingData(message);
            this.loginService
              .sendEmail(mailingData)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.toastr.success('Please check your E-mail address inbox.', 'Password Reset');
                  this.errorMessage = '';
                },
                error: (err) => {
                  this.errorMessage = err.message || 'Failed to send email. Please try again.';
                  this.toastr.error(this.errorMessage, 'Error');
                },
              });
          } else {
            this.errorMessage = 'E-mail address does not exist.';
            this.toastr.error(this.errorMessage, 'Error');
          }
        },
        error: (err) => {
          this.errorMessage = err.message || 'Failed to process request. Please try again.';
          this.toastr.error(this.errorMessage, 'Error');
        },
      });
  }

  private buildPasswordResetMessage(password: string): string {
    return `
      <div style='border: 1px solid #000000; border-radius: 5px; width: 618px;'>
        <img style='border-radius: 5px;' src='http://www.aucdt.edu.gh/images/AUCDT-letterhead.png'/>
        <div style='padding: 10px;'>
          ${this.getPasswordMessage(password)}
        </div>
      </div>
    `;
  }

  private buildMailingData(message: string): string {
    return JSON.stringify({
      senderEmailId: this.loginService.getSenderEmailId(),
      receiverEmailId: this.usermail,
      message: message,
      subject: 'Requested info from admissions-dev.aucdt.edu.gh',
    });
  }

  getPasswordMessage(pwd: string): string {
    return 'Your password is : ' + pwd;
  }

  captureCredential(formData: any) {
    this.errorMessage = '';
    if (!this.validateInput(formData)) return;
    if (!this.captchaResolved) {
      this.errorMessage = 'Please confirm you are not a robot.';
      this.toastr.warning(this.errorMessage, 'Captcha Required');
      return;
    }
    if (!this.validateDeadLineDate()) {
      this.errorMessage = 'Application deadline has passed.';
      this.toastr.error(this.errorMessage, 'Deadline Expired');
      this.router.navigateByUrl('./deadline.html');
      return;
    }
    this.usermail = formData.email;
    this.password = formData.password;
    this.performLogin();
  }

  private validateInput(formData: any): boolean {
    if (!formData.email?.trim()) {
      this.errorMessage = 'E-mail address is required.';
      this.toastr.error(this.errorMessage, 'Validation Error');
      return false;
    }
    if (!formData.password?.trim()) {
      this.errorMessage = 'Password is required.';
      this.toastr.error(this.errorMessage, 'Validation Error');
      return false;
    }
    return true;
  }

  private performLogin() {
    this.isLoading = true;
    this.errorMessage = '';
    this.loginService
      .authenticateUser(this.usermail.trim(), this.password.trim())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (response) => this.handleLoginSuccess(response),
        error: (error) => this.handleLoginError(error),
      });
  }

  private handleLoginSuccess(response: any) {
    if (response?.status === 200) {
      this.errorMessage = '';
      
      console.log('📥 Login response received:', {
        status: response.status,
        hasToken: !!response.token,
        hasName: !!response.name,
        hasPhone: !!response.phoneNumber,
      });

      // This ensures data is written to localStorage before component unmounts
      this.setUserSession(response);
      
      // CRITICAL FIX: Force a synchronous verification that ALL data was stored
      const verificationResult = this.verifySessionStorage(response);
      
      if (!verificationResult.success) {
        console.error('❌ Session storage verification failed');
        console.error('❌ Missing keys:', verificationResult.missingKeys);
        this.errorMessage = 'Failed to store session data. Please try again.';
        this.toastr.error(this.errorMessage, 'Storage Error');
        return;
      }
      
      console.log('✅ All session data verified and stored correctly');
      console.log('✅ Verified keys:', verificationResult.verifiedKeys);
      
      this.toastr.success('Login successful. Redirecting to payment...', 'Welcome!');
      
      // before navigation, then add timeout for extra safety
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.navigateToPayment();
        }, 300); // Increased to 300ms for maximum reliability
      });
    } else {
      this.errorMessage = 'An unexpected error occurred. Please try again.';
      this.toastr.error(this.errorMessage, 'Error');
    }
  }

  /**
   * NEW METHOD: Verify that all critical session data was stored correctly
   */
  private verifySessionStorage(response: any): { success: boolean; missingKeys: string[]; verifiedKeys: string[] } {
    const criticalKeys = {
      'usermail': this.usermail,
      'token': response.token,
      'authToken': response.token,
      'userName': response.name || '',
      'userPhone': response.phoneNumber || '',
      'tokenType': response.tokenType || 'Bearer',
    };

    const missingKeys: string[] = [];
    const verifiedKeys: string[] = [];

    Object.entries(criticalKeys).forEach(([key, expectedValue]) => {
      const storedValue = localStorage.getItem(key);
      
      if (!storedValue || (expectedValue && storedValue !== expectedValue)) {
        missingKeys.push(key);
        console.error(`❌ Verification failed for key "${key}":`, {
          expected: expectedValue,
          stored: storedValue,
        });
      } else {
        verifiedKeys.push(key);
      }
    });

    return {
      success: missingKeys.length === 0,
      missingKeys,
      verifiedKeys,
    };
  }

  /**
   * NEW METHOD: Separate navigation logic for better control
   */
  private navigateToPayment(): void {
    console.log('🚀 Initiating navigation to /auth-payment');
    console.log('🔍 Pre-navigation localStorage state:', {
      token: !!localStorage.getItem('token'),
      authToken: !!localStorage.getItem('authToken'),
      usermail: localStorage.getItem('usermail'),
      userName: localStorage.getItem('userName'),
    });
    console.log('🔍 Current router URL:', this.router.url);

    // CRITICAL FIX: Use absolute path and ensure navigation happens
    this.router.navigate(['/auth-payment'], {
      // Add these options to ensure navigation isn't blocked
      skipLocationChange: false,
      replaceUrl: false,
    }).then(
      (navigationSuccess) => {
        if (navigationSuccess) {
          console.log('✅ Navigation to /auth-payment succeeded');
          console.log('✅ New router URL:', this.router.url);
        } else {
          console.error('❌ Navigation to /auth-payment was blocked or failed');
          console.error('❌ Current router URL:', this.router.url);
          console.error('❌ This usually means a guard blocked the navigation');
          
          // LAST RESORT: Force navigation using window.location
          console.log('🔄 Attempting fallback navigation using window.location.hash');
          window.location.hash = '/auth-payment';
        }
      },
      (navigationError) => {
        console.error('❌ Navigation error:', navigationError);
        console.error('❌ Error details:', {
          message: navigationError.message,
          stack: navigationError.stack,
        });
        
        // LAST RESORT: Force navigation
        window.location.hash = '/auth-payment';
      }
    );
  }

  private handleLoginError(error: any) {
    const displayMessage = error.message || 'An unknown error occurred. Please try again.';
    const statusCode = error.status;

    const statusTitleMap: { [key: number]: string } = {
      0: 'Connection Error',
      400: 'Invalid Input',
      401: 'Authentication Failed',
      403: 'Access Denied',
      404: 'Account Not Found',
      500: 'Server Error',
      503: 'Service Unavailable',
    };

    let displayTitle = statusCode ? statusTitleMap[statusCode] || 'Login Error' : 'Login Error';

    this.errorMessage = displayMessage;
    this.toastr.error(displayMessage, displayTitle);
    console.error('Login failed:', error.originalError || error);
  }

  private setUserSession(response: any) {
    // CRITICAL FIX: Clear old data first to prevent any conflicts
    const keysToRemove = ['usermail', 'token', 'authToken', 'userName', 'userPhone', 'tokenType', 'loginTimestamp'];
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    console.log('🧹 Cleared old session data');
    
    // CRITICAL FIX: Store data with explicit verification after each write
    const token = response.token;
    
    // Store each key and verify immediately
    const sessionData = {
      'usermail': this.usermail,
      'token': token,
      'authToken': token, // CRITICAL: Store in BOTH locations
      'userName': response.name || '',
      'userPhone': response.phoneNumber || '',
      'tokenType': response.tokenType || 'Bearer',
      'loginTimestamp': new Date().toISOString(),
    };

    console.log('💾 Storing session data...');
    
    Object.entries(sessionData).forEach(([key, value]) => {
      localStorage.setItem(key, value);
      const verified = localStorage.getItem(key);
      
      if (verified !== value) {
        console.error(`❌ Failed to store ${key}`);
      } else {
        console.log(`✅ Stored and verified ${key}`);
      }
    });
    
    // CRITICAL: Also backup to sessionStorage for redundancy
    Object.entries(sessionData).forEach(([key, value]) => {
      sessionStorage.setItem(`backup_${key}`, value);
    });
    
    console.log('💾 Session data stored in both localStorage and sessionStorage');
    console.log('💾 Total localStorage keys:', Object.keys(localStorage).length);
  }

  validateDeadLineDate(): boolean {
    const submissionLatestDate = new Date(3019, 2, 5, 23, 59, 59, 59);
    const todayDate = new Date();
    return todayDate < submissionLatestDate;
  }
}